import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  Button,
  FormFeedback,
} from "reactstrap";
import { FiTrash2, FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import "./AddJob.css";
import { handeljobAdd } from "../../services/api";

function AddJob() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    department: "",
    location: "",
    employmentType: "",
    experienceRequired: "",
    salaryRange: "",
    applicationDeadline: "",
    status: "Draft",
    requiredSkills: "",
    description: "",
    keyResponsibilities: [""], // dynamic list
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ---- Key Responsibilities handlers ----
  const handleResponsibilityChange = (index, value) => {
    const updated = [...formData.keyResponsibilities];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, keyResponsibilities: updated }));
  };

  const addResponsibility = () => {
    setFormData((prev) => ({
      ...prev,
      keyResponsibilities: [...prev.keyResponsibilities, ""],
    }));
  };

  const removeResponsibility = (index) => {
    if (formData.keyResponsibilities.length === 1) return; // keep at least one
    const updated = formData.keyResponsibilities.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, keyResponsibilities: updated }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Job Title is required";
    if (!formData.department.trim()) newErrors.department = "Department is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.employmentType) newErrors.employmentType = "Employment Type is required";
    if (!formData.experienceRequired.trim())
      newErrors.experienceRequired = "Experience is required";
    if (!formData.applicationDeadline)
      newErrors.applicationDeadline = "Deadline is required";
    if (!formData.requiredSkills.trim()) newErrors.requiredSkills = "Skills are required";
    if (!formData.description.trim()) newErrors.description = "Description is required";

    // Optional: validate at least one non-empty responsibility
    const filledResponsibilities = formData.keyResponsibilities.filter((r) =>
      r.trim()
    );
    if (filledResponsibilities.length === 0) {
      newErrors.keyResponsibilities = "Add at least one key responsibility";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Clean empty strings from keyResponsibilities
      const cleanedResponsibilities = formData.keyResponsibilities
        .map((r) => r.trim())
        .filter(Boolean);

      const payload = {
        title: formData.title,
        department: formData.department,
        location: formData.location,
        employmentType: formData.employmentType,
        experienceRequired: formData.experienceRequired,
        salaryRange: formData.salaryRange || undefined,
        description: formData.description,
        requiredSkills: formData.requiredSkills,
        applicationDeadline: formData.applicationDeadline,
        status: formData.status,
        keyResponsibilities: cleanedResponsibilities, // array for JSONB
      };

      const response = await handeljobAdd(payload);

      if (response?.message === "Job created") {
      toast.success(
        formData.status === "Published"
          ? "Job Published Successfully!"
          : "Job Saved as Draft!",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        }
      );
        navigate("/admin/dashboard");
      } else {
        setApiError(response?.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      setApiError(err.message || "Failed to create job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-job-page">
      <Container fluid className="add-job-container py-2 py-lg-2">
        {/* Header */}
        <div className="add-job-header mb-3">
          <h1 className="add-job-title">Add New Job</h1>
          <p className="add-job-subtitle">Create a new job posting</p>
        </div>

        {apiError && <div className="alert alert-danger mb-4">{apiError}</div>}

        <Card className="add-job-card shadow border-0">
          <CardBody className="p-2 p-md-4">
            <Form onSubmit={handleSubmit}>
              <Row className="g-4">
                {/* Job Title */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">
                      Job Title <span className="required">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="title"
                      placeholder="e.g. Senior Developer"
                      value={formData.title}
                      onChange={handleChange}
                      invalid={!!errors.title}
                      className="add-job-input"
                    />
                    {errors.title && <FormFeedback>{errors.title}</FormFeedback>}
                  </FormGroup>
                </Col>

                {/* Department */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">
                      Department <span className="required">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="department"
                      placeholder="e.g. Engineering"
                      value={formData.department}
                      onChange={handleChange}
                      invalid={!!errors.department}
                      className="add-job-input"
                    />
                    {errors.department && (
                      <FormFeedback>{errors.department}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                {/* Location */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">
                      Location <span className="required">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="location"
                      placeholder="e.g. New York, NY"
                      value={formData.location}
                      onChange={handleChange}
                      invalid={!!errors.location}
                      className="add-job-input"
                    />
                    {errors.location && (
                      <FormFeedback>{errors.location}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                {/* Employment Type */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">
                      Employment Type <span className="required">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="employmentType"
                      value={formData.employmentType}
                      onChange={handleChange}
                      invalid={!!errors.employmentType}
                      className="add-job-select"
                    >
                      <option value="" disabled>
                        Select type
                      </option>
                      <option>Full-time</option>
                      <option>Part-time</option>
                      <option>Contract</option>
                      <option>Internship</option>
                    </Input>
                    <FormFeedback>{errors.employmentType}</FormFeedback>
                  </FormGroup>
                </Col>

                {/* Experience Required */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">
                      Experience Required <span className="required">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="experienceRequired"
                      placeholder="e.g. 4-7 years"
                      value={formData.experienceRequired}
                      onChange={handleChange}
                      invalid={!!errors.experienceRequired}
                      className="add-job-input"
                    />
                    {errors.experienceRequired && (
                      <FormFeedback>{errors.experienceRequired}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                {/* Salary Range */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">Salary Range</Label>
                    <Input
                      type="text"
                      name="salaryRange"
                      placeholder="e.g. ₹12-18 LPA"
                      value={formData.salaryRange}
                      onChange={handleChange}
                      className="add-job-input"
                    />
                  </FormGroup>
                </Col>

                {/* Application Deadline */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">
                      Application Deadline <span className="required">*</span>
                    </Label>
                    <InputGroup>
                      <Input
                        type="date"
                        name="applicationDeadline"
                        value={formData.applicationDeadline}
                        onChange={handleChange}
                        invalid={!!errors.applicationDeadline}
                        className="add-job-input"
                      />
                    </InputGroup>
                    {errors.applicationDeadline && (
                      <FormFeedback>{errors.applicationDeadline}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                {/* Status */}
                <Col md={6}>
                  <FormGroup>
                    <Label className="add-job-label">Status</Label>
                    <Input
                      type="select"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="add-job-select"
                    >
                      <option>Draft</option>
                      <option>Published</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {/* Skills */}
              <FormGroup className="mt-4">
                <Label className="add-job-label">
                  Required Skills <span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  name="requiredSkills"
                  placeholder="React, TypeScript, Redux, Node.js (comma separated)"
                  value={formData.requiredSkills}
                  onChange={handleChange}
                  invalid={!!errors.requiredSkills}
                  className="add-job-input"
                />
                {errors.requiredSkills && (
                  <FormFeedback>{errors.requiredSkills}</FormFeedback>
                )}
              </FormGroup>
{/* ========== KEY RESPONSIBILITIES (Dynamic List) ========== */}
              <FormGroup className="mt-4">
                <Label className="add-job-label">
                  Key Responsibilities <span className="required">*</span>
                </Label>

                <div className="add-job-key-resp-box">
                  {formData.keyResponsibilities.map((item, index) => (
                    <div key={index} className="add-job-key-resp-row">
                      <Input
                        type="text"
                        placeholder={`e.g. Responsibility ${index + 1}`}
                        value={item}
                        onChange={(e) =>
                          handleResponsibilityChange(index, e.target.value)
                        }
                        className="add-job-key-resp-input"
                      />
                      <button
                        type="button"
                        className="add-job-key-resp-delete"
                        onClick={() => removeResponsibility(index)}
                        disabled={formData.keyResponsibilities.length === 1}
                        title="Remove"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-job-key-resp-add"
                    onClick={addResponsibility}
                  >
                    <FiPlus size={16} className="me-1" />
                    Add Key Responsibility
                  </button>
                </div>

                {errors.keyResponsibilities && (
                  <div className="text-danger small mt-2">
                    {errors.keyResponsibilities}
                  </div>
                )}
              </FormGroup>
              {/* Job Description */}
              <FormGroup className="mt-4">
                <Label className="add-job-label">
                  Job Description <span className="required">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="description"
                  rows="8"
                  placeholder="Describe the role, responsibilities, and requirements..."
                  value={formData.description}
                  onChange={handleChange}
                  invalid={!!errors.description}
                  className="add-job-textarea"
                />
                {errors.description && (
                  <FormFeedback>{errors.description}</FormFeedback>
                )}
              </FormGroup>

              

              {/* Buttons */}
              <div className="add-job-actions mt-5 d-flex gap-3 flex-wrap">
                <Button
                  type="submit"
                  color="success"
                  className="add-job-save-btn px-5 py-3"
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : formData.status === "Published"
                    ? "Publish Job"
                    : "Save as Draft"}
                </Button>
                <Button
                  type="button"
                  color="light"
                  outline
                  className="add-job-cancel-btn px-5 py-3"
                  onClick={() => navigate("/admin/dashboard")}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export default AddJob;